import { axiosInstance, get } from "./apiService";

interface ApiErrorPayload {
  message?: string;
}

interface ApiErrorLike extends Error {
  response?: {
    data?: ApiErrorPayload;
  };
}

export interface BusinessProfileFeature {
  key: string;
  name: string;
}

export interface BusinessProfileRole {
  key: string;
  name: string;
  features: BusinessProfileFeature[];
}

export interface PersonalBusinessProfile {
  type: "personal";
  displayName: string;
  avatarUrl: string | null;
}

export interface BusinessProfileSummary {
  type: "business";
  businessId: string;
  businessName: string;
  logoUrl: string | null;
  avatarUrl?: string | null;
  logo?: string | null;
  imageUrl?: string | null;
  role: BusinessProfileRole | null;
  kybStatus?: string;
  membershipStatus: string;
}

export interface BusinessProfilesPayload {
  personal: PersonalBusinessProfile;
  businesses: BusinessProfileSummary[];
  activeBusinessId: string | null;
}

export interface ActiveBusinessPayload {
  activeBusinessId: string | null;
}

export interface BusinessListItem {
  id?: string;
  _id?: string;
  businessName: string;
  status: string;
  kycStatus?: string;
  kybStatus?: string;
  logoUrl?: string | null;
  avatarUrl?: string | null;
  logo?: string | null;
  imageUrl?: string | null;
  role?: { key: string; name: string } | null;
}

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    const apiError = error as ApiErrorLike;
    return apiError.response?.data?.message || error.message;
  }

  return "Unknown error";
};

export const getBusinessProfiles = async () => {
  try {
    return await get("/api/v2/business/profiles");
  } catch (error: unknown) {
    throw new Error(
      `Could not get business profiles: ${getErrorMessage(error)}`
    );
  }
};

export const setActiveBusiness = async (businessId: string | null) => {
  try {
    return await axiosInstance.patch("/api/v2/business/me/active-business", {
      businessId,
    });
  } catch (error: unknown) {
    throw new Error(
      `Could not update active business: ${getErrorMessage(error)}`
    );
  }
};

export const listBusinesses = async () => {
  try {
    return await get("/api/v2/business");
  } catch (error: unknown) {
    throw new Error(`Could not list businesses: ${getErrorMessage(error)}`);
  }
};
